import { useSyncStatus } from "../data/StoreContext";
import { cn } from "../ui";

/** "Synced", "N changes waiting to sync", or "Offline" (SPEC §9.2 unsynced badge). */
export function syncLabel(status: {
  connection: string;
  unsynced: number;
  hasUnsynced: boolean;
  unsyncedFromEarlier?: boolean;
  authFailed: boolean;
}): {
  text: string;
  tone: "ok" | "wait" | "off";
} {
  if (status.authFailed) {
    return { text: "Log in again to sync", tone: "off" };
  }
  if (status.unsynced > 0 && !status.unsyncedFromEarlier) {
    return {
      text:
        status.unsynced === 1
          ? "1 change waiting to sync"
          : `${status.unsynced} changes waiting to sync`,
      tone: "wait",
    };
  }
  if (status.hasUnsynced || status.unsynced > 0) {
    return { text: "Changes waiting to sync", tone: "wait" };
  }
  if (status.connection === "online") {
    return { text: "Synced", tone: "ok" };
  }
  return { text: status.connection === "connecting" ? "Connecting…" : "Offline", tone: "off" };
}

export function SyncIndicator({ className }: { className?: string }) {
  const status = useSyncStatus();
  const { text, tone } = syncLabel(status);
  return (
    <span
      className={cn("flex items-center gap-2 text-[13px] text-muted", className)}
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-2 rounded-full",
          tone === "ok" && "bg-[#5E8F55]",
          tone === "wait" && "bg-[#C9A23A]",
          tone === "off" && "bg-faint",
        )}
      />
      {text}
    </span>
  );
}
